import { useQuery } from "@tanstack/react-query";

import { queryKeys } from "@/shared/utils/queryKeys";
import { getAccessToken } from "@/shared/types/auth";

export interface CalendlyBooking {
  event_uri: string;
  invitee_uri: string;
  source: string;
  booked_at: string;
  start_time: string | null;
  end_time: string | null;
  event_name: string | null;
  event_status: string | null;
}

async function fetchCalendlyBooking(estateId: number, source: string): Promise<CalendlyBooking> {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error("Authentication required");
  }

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/communications/${estateId}/calendly-booking/${source}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
  });

  if (response.status === 404) {
    throw new NotFoundError();
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch calendly booking: ${response.status}`);
  }

  const data = await response.json();
  return data as CalendlyBooking;
}

class NotFoundError extends Error {
  constructor() {
    super("Not found");
    this.name = "NotFoundError";
  }
}

export function useCalendlyBooking(estateId: number | undefined, source: string) {
  const query = useQuery({
    queryKey: queryKeys.calendly.booking(estateId ?? 0, source),
    queryFn: () => fetchCalendlyBooking(estateId!, source),
    enabled: !!estateId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => {
      if (error instanceof NotFoundError) return false;
      return failureCount < 2;
    },
  });

  const booking = query.data ?? null;
  const hasBooking = !!booking && booking.event_status !== "canceled";

  return {
    booking: hasBooking ? booking : null,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}
